// MST
import { types, flow, cast, detach } from 'mobx-state-tree';

// Interfaces
import {
  IAssessmentGeneralProofGetListAttrs,
  IAssessmentGeneralProofDownloadDocumentAttrs,
} from 'interfaces/back-office/assessment/registration/general-proof/general-proof-view.interface';
import {
  IAssessmentGeneralProofApiListResponse,
  IAssessmentGeneralProofApiDownloadDocumentResponse,
} from 'interfaces/back-office/assessment/registration/general-proof/general-proof-api.interface';
import { ISharedCommonLoadingAttrs, ISharedCommonLoadingQuestionSetAttrs } from 'interfaces/shared/shared-common.interface';

// Extensions
import { withEnvironment } from 'models/extensions/with-environment';

// Constant
import { LOADING, LOADING_KEY_TYPE } from 'constant/shared.constant';

// Utils
import { withHelper_fileDownload } from 'models/extensions/with-helper';
import {
  withHelper_questionSetModel,
  withHelper_commonLoadingModel,
  withHelper_commonMapQuestionSet,
  withHelper_commonMapQuestionSetLoading,
  withHelper_commonMapLoading,
} from 'models/extensions/helper';

// Content Disposition
import contentDisposition from 'content-disposition'

// Types
const { optional, model, array } = types;

// Loading
const { BASE, DOWNLOAD } = LOADING;
const { ANSWER_FILES } = LOADING_KEY_TYPE;

// Base Model
const AssessmentGeneralProofStoreModel = model('AssessmentGeneralProof')
  .props({
    assessmentGeneralProof_loading: withHelper_commonLoadingModel,
    assessmentGeneralProof_list: optional(array(withHelper_questionSetModel), []),
  })
  .actions(self => ({
    assessmentGeneralProof_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.assessmentGeneralProof_loading, payload);

      detach(self.assessmentGeneralProof_loading);
      self.assessmentGeneralProof_loading = cast(map);
    },
    assessmentGeneralProof_SET_LIST: (payload: IAssessmentGeneralProofApiListResponse['res']['data']): void => {
      const map = withHelper_commonMapQuestionSet(payload);
      self.assessmentGeneralProof_list = cast(map);
      // const iniApa = detach(self.assessmentGeneralProof_list);
      // console.log(iniApa, 'ini apa');
    },
    assessmentGeneralProof_MAP_LOADING_LIST: (payload: ISharedCommonLoadingQuestionSetAttrs): void => {
      const map = withHelper_commonMapQuestionSetLoading(self.assessmentGeneralProof_list, payload);

      detach(self.assessmentGeneralProof_list);
      self.assessmentGeneralProof_list = cast(map);
    },
    assessmentGeneralProof_CLEAR_LIST: (): void => {
      self.assessmentGeneralProof_list = cast([]);
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch assessment general proof list
     *
     * @param {IAssessmentGeneralProofGetListAttrs} payload
     *
     * @return {any} any
     */
    assessmentGeneralProof_fetchList: flow(function* (payload: IAssessmentGeneralProofGetListAttrs) {
      self.assessmentGeneralProof_SET_LOADING({ type: BASE, value: true });

      const response: IAssessmentGeneralProofApiListResponse =
        yield self.environment.assessmentGeneralProofApi.assessmentGeneralProof_getList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        self.assessmentGeneralProof_SET_LIST(response.res.data);

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentGeneralProof_SET_LOADING({ type: BASE, value: false });
    }),

    /**
     * @description Download assessment general proof document
     *
     * @param {IAssessmentGeneralProofDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    assessmentGeneralProof_downloadDocument: flow(function* (payload: IAssessmentGeneralProofDownloadDocumentAttrs) {
      const { blobId, questionSetId, resultId, resultDetailId, fileId } = payload.params;

      // Start Loading
      self.assessmentGeneralProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: DOWNLOAD,
        value: true,
      });

      const response: IAssessmentGeneralProofApiDownloadDocumentResponse =
        yield self.environment.assessmentGeneralProofApi.assessmentGeneralProof_downloadDocument({
          params: { blobId },
          requestConfig: { ...payload.requestConfig },
        });
      
      if (response.ok) {
        const _contentDisposition = response.headers?.["content-disposition"] || ""
        const filename = contentDisposition.parse(_contentDisposition).parameters?.filename || "";
        const fileType = filename.split(".")[filename.split(".").length - 1];
        let mimeType = "";

        if (fileType === "pdf") {
          mimeType = 'application/pdf'
        } else if (fileType === "jpg" || fileType === "jpeg" || fileType === "png") {
          mimeType = `image/${fileType}`
        } else {
          withHelper_fileDownload(response.res, response.headers!)
          // Do when response is success
          if (payload?.onSuccess) payload.onSuccess() 
          return 
        }
        
        const file = new File([response.res], filename, {
          type: mimeType
        });
        const url = URL.createObjectURL(file);
        window.open(url)

        // Do when response is success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      // Stop Loading
      self.assessmentGeneralProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: DOWNLOAD,
        value: false,
      });
    }),
  }));

export const createAssessmentGeneralProofStoreDefaultModel = () => optional(AssessmentGeneralProofStoreModel, {});
